import type { PrinterView } from 'shared-models/browser';

export const FIXED_PRINTER_STORAGE_KEY = 'posFixedPrinterId';

// Reads the dedicated fixed-printer key only; the manager flow is the sole writer/clearer.
export function getFixedPrinterIdString(): string | null {
  return localStorage.getItem(FIXED_PRINTER_STORAGE_KEY);
}

export function getFixedPrinterId(): number | null {
  const raw = getFixedPrinterIdString();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function setFixedPrinterId(printerId: number): void {
  localStorage.setItem(FIXED_PRINTER_STORAGE_KEY, String(printerId));
}

export function clearFixedPrinterId(): void {
  localStorage.removeItem(FIXED_PRINTER_STORAGE_KEY);
}

// Returns the stored id only when its printer is still active. Print and drawer eligibility
// are intentionally evaluated separately at the action boundary.
export function resolveFixedPrinter(activePrinters: PrinterView[]): number | null {
  const id = getFixedPrinterId();
  if (id == null) return null;
  const match = activePrinters.find((p) => p.id === id && p.active);
  return match ? id : null;
}

export function fixedPrinterNeedsDrawerChoice(activePrinters: PrinterView[]): boolean {
  const fixedPrinterId = resolveFixedPrinter(activePrinters);
  return fixedPrinterId != null && !activePrinters.some((printer) => printer.id === fixedPrinterId && printer.cashDrawer);
}

export type PrintTarget =
  | { kind: 'print'; printerId: number }
  | { kind: 'pick'; candidates?: PrinterView[] }
  | { kind: 'none' };

// Shared print-routing ladder: fixed wins, else sole active prints, 2+ picks, 0 = none.
export function resolvePrintTarget(activePrinters: PrinterView[], fixedPrinterId: number | null): PrintTarget {
  if (fixedPrinterId != null) return { kind: 'print', printerId: fixedPrinterId };
  if (activePrinters.length === 1) return { kind: 'print', printerId: activePrinters[0].id };
  if (activePrinters.length > 1) return { kind: 'pick' };
  return { kind: 'none' };
}

// The usable billable printers among the active list (list is already active && !human_disabled).
export function billablePrinters(activePrinters: PrinterView[]): PrinterView[] {
  return activePrinters.filter((p) => p.billable);
}

// A valid pinned billable printer owns receipt-class printing. If it loses billable status,
// require an explicit printer choice rather than silently sending to another printer.
export function resolveReceiptPrintTarget(activePrinters: PrinterView[]): PrintTarget {
  const fixedPrinterId = resolveFixedPrinter(activePrinters);
  if (fixedPrinterId != null) {
    const fixedPrinter = activePrinters.find((printer) => printer.id === fixedPrinterId);
    if (fixedPrinter?.billable) {
      return { kind: 'print', printerId: fixedPrinterId };
    }
    const billables = billablePrinters(activePrinters);
    return { kind: 'pick', ...(billables.length > 0 ? { candidates: billables } : {}) };
  }
  const billables = billablePrinters(activePrinters);
  if (billables.length === 1) return { kind: 'print', printerId: billables[0].id };
  if (billables.length >= 2) return { kind: 'pick', candidates: billables };
  return resolvePrintTarget(activePrinters, null);
}

// Sole auto-target id for gates/silent-print seeds; null when a pick is needed or nothing routes.
export function resolveReceiptAutoPrinterId(activePrinters: PrinterView[]): number | null {
  const target = resolveReceiptPrintTarget(activePrinters);
  return target.kind === 'print' ? target.printerId : null;
}

// A pinned printer with a drawer owns drawer operations even if it is no longer billable.
// A pinned printer without a drawer returns null so the caller opens the drawer picker.
export function resolveBillableDrawerId(activePrinters: PrinterView[]): number | null {
  const fixedPrinterId = resolveFixedPrinter(activePrinters);
  if (fixedPrinterId != null) {
    const fixedPrinter = activePrinters.find((printer) => printer.id === fixedPrinterId);
    return fixedPrinter?.cashDrawer ? fixedPrinterId : null;
  }
  const billableDrawers = activePrinters.filter((p) => p.billable && p.cashDrawer);
  if (billableDrawers.length === 1) return billableDrawers[0].id;
  if (billableDrawers.length >= 2) return null;
  return null;
}
